/**
 * Timing instrumentation for the ISR Workspace init / outreach / sales-save
 * debug mission.
 *
 * Every log is prefixed with "⏱️" so the whole trace is grep-able in the
 * console. Logs are grouped by `group` (init | outreach | salesSave | mutation)
 * and carry an optional `traceId` so all logs belonging to one user action
 * cluster together — e.g. one "Start outreach" click emits 4-5 logs that
 * share the same traceId.
 *
 * Flip ENABLED to false to silence every timing log in one place — no need
 * to hunt console.log calls across files when the debug phase is over.
 */

const ENABLED = true;

/** Monotonic counter for Appwrite createExecution calls — answers
 *  "how many calls did this interaction trigger?" at a glance. */
let _execCallCount = 0;
export const nextExecNum = (): number => ++_execCallCount;
export const getExecCount = (): number => _execCallCount;

/** Short random id (8 chars) used to correlate logs from one user action. */
export const newTraceId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ? uuid.slice(0, 8) : String(Date.now()).slice(-8);
};

type LogData = Record<string, unknown>;

/**
 * Emit a timing log. `data` entries are rendered as `key=value` pairs after
 * the label so the console line stays scannable:
 *
 *   ⏱️ [outreach:abc123] startOutreach click campaign=123 lead=456
 *
 * Pass `durationMs` inside `data` when measuring an async boundary — callers
 * compute it with `performance.now()` so the stamp is precise.
 */
export const timingLog = (
  group: string,
  label: string,
  data?: LogData,
): void => {
  if (!ENABLED) return;
  const parts = [`⏱️ [${group}] ${label}`];
  if (data) {
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === null) continue;
      parts.push(`${key}=${typeof value === 'number' ? (Number.isInteger(value) ? value : value.toFixed(1)) : value}`);
    }
  }
  console.log(...parts);
};

/**
 * Convenience wrapper for async-boundary timing — wraps a promise and logs
 * start + complete (or error) with the elapsed duration. Returns the promise's
 * own result so it can be dropped into `await` expressions transparently.
 *
 *   const result = await timingAsync('outreach:abc', 'sendToOutreach',
 *     n8nService.sendToOutreach({...}), { campaign, lead });
 */
export const timingAsync = async <T>(
  group: string,
  label: string,
  promise: Promise<T>,
  data?: LogData,
): Promise<T> => {
  if (!ENABLED) return promise;
  const t0 = performance.now();
  timingLog(group, `${label} start`, data);
  try {
    const result = await promise;
    timingLog(group, `${label} complete`, { ...data, durationMs: performance.now() - t0 });
    return result;
  } catch (error) {
    timingLog(group, `${label} error`, { ...data, durationMs: performance.now() - t0, error: (error as Error)?.message });
    throw error;
  }
};
